import React, { useCallback, useEffect, useMemo } from 'react'
import { Temporal } from '@js-temporal/polyfill'
import clsx from 'clsx'
import {
  useTimeField,
  useDateSegment,
  type TimeValue,
  DateValue,
} from 'react-aria'
import { useTimeFieldState } from 'react-stately'
import type { DateFieldState, DateSegment } from 'react-stately'

import { useMergeRefs } from '../../hooks/useMergeRefs'
import { DropdownMenu, type DropdownMenuContentProps } from '../DropdownMenu'
import { ScrollArea } from '../ScrollArea'

import styles from './TimeInput.module.css'

type HandleInteractOutsideEvent = NonNullable<
  DropdownMenuContentProps['onInteractOutside']
> extends (event: infer E) => void
  ? E
  : never

/**
 * Converts a Temporal.PlainTime object to a TimeValue object for React Aria
 *
 * @param plainTime - The Temporal.PlainTime object to convert
 * @returns A TimeValue object compatible with React Aria, or undefined if no input is provided
 */
function plainTimeToTimeValue(
  plainTime?: Temporal.PlainTime,
): TimeValue | undefined {
  if (!plainTime) return undefined

  return {
    hour: plainTime.hour,
    minute: plainTime.minute,
    second: plainTime.second,
  } as unknown as TimeValue
}

/**
 * Converts a React Aria TimeValue object to a Temporal.PlainTime object
 *
 * @param timeValue - The TimeValue object to convert
 * @returns A Temporal.PlainTime object, or null if no input is provided
 */
function timeValueToPlainTime(
  timeValue?: TimeValue,
): Temporal.PlainTime | null {
  if (!timeValue) return null

  return new Temporal.PlainTime(
    timeValue.hour ?? 0,
    timeValue.minute ?? 0,
    timeValue.second ?? 0,
  )
}

function isEventOverlappingRef(
  e: HandleInteractOutsideEvent,
  ref: HTMLElement | null,
) {
  if (!ref) return false
  const triggerRect = ref.getBoundingClientRect()

  // Only handle pointer events, not focus events
  if (e.type !== 'dismissableLayer.pointerDownOutside') {
    return false
  }

  const pointerEvent = e.detail.originalEvent as PointerEvent
  const pointerCoordinates = {
    x: pointerEvent.clientX,
    y: pointerEvent.clientY,
  }
  return (
    pointerCoordinates.x > triggerRect.x &&
    pointerCoordinates.y > triggerRect.y &&
    pointerCoordinates.x < triggerRect.x + triggerRect.width &&
    pointerCoordinates.y < triggerRect.y + triggerRect.height
  )
}

interface TimeSegmentProps {
  segment: DateSegment
  state: DateFieldState
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  focusRef?: React.MutableRefObject<HTMLDivElement | null>
}

/**
 * Individual time segment component (hour, minute, second, etc.)
 */
function TimeSegment({
  segment,
  state,
  onKeyDown,
  focusRef,
}: TimeSegmentProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const { segmentProps } = useDateSegment(segment, state, ref)

  return (
    <div
      {...segmentProps}
      ref={ref}
      className={clsx(
        styles.segment,
        segment.type === 'literal' && styles.literal,
        segment.type === 'hour' && styles.hour,
        segment.type === 'minute' && styles.minute,
        segment.type === 'second' && styles.second,
        segment.isPlaceholder && styles.placeholder,
      )}
      style={{
        ...segmentProps.style,
      }}
      onKeyDown={(e) => {
        segmentProps.onKeyDown?.(e)
        if (onKeyDown) {
          onKeyDown(e)
        }
      }}
      onFocus={(e) => {
        segmentProps.onFocus?.(e)
        if (segment.type !== 'literal' && focusRef) {
          focusRef.current = ref.current
        }
      }}
      role={segment.type !== 'literal' ? 'spinbutton' : undefined}
    >
      {segment.text}
    </div>
  )
}

type DayPeriod = 'AM' | 'PM'

export interface TimeInputProps {
  /**
   * The current value (controlled)
   * Uses the Temporal API PlainTime object from @js-temporal/polyfill
   */
  value?: Temporal.PlainTime
  /**
   * The default value (uncontrolled)
   * Uses the Temporal API PlainTime object from @js-temporal/polyfill
   */
  defaultValue?: Temporal.PlainTime
  /** Handler that is called when the value changes */
  onChange?: (value: Temporal.PlainTime | null) => void
  /** Whether the time field is disabled */
  disabled?: boolean
  /** Whether the time field is read only */
  readOnly?: boolean
  /** Whether the time field is required */
  required?: boolean
  /** Whether to use 12 or 24 hour format
   * @default Based on user's locale
   */
  hourCycle?: 12 | 24
  /** The granularity of the time field
   * @default 'minute'
   */
  granularity?: 'hour' | 'minute' | 'second'
  /** The minimum allowed time */
  min?: Temporal.PlainTime
  /** The maximum allowed time */
  max?: Temporal.PlainTime
  /** The placeholder value */
  placeholder?: Temporal.PlainTime
  /** Additional CSS class for the time field */
  className?: string
  /** Size of the input
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Element id for accessibility association with label
   */
  id?: string
}

/**
 * Time field component that allows users to enter and edit time values using a keyboard.
 * Each part of a time value (hours, minutes, seconds) is displayed in an individually editable segment.
 */
export const TimeInput = React.forwardRef<HTMLDivElement, TimeInputProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      disabled,
      readOnly,
      required,
      hourCycle = 12, // Default to 12-hour format to match the design
      granularity = 'minute',
      min,
      max,
      placeholder,
      className,
      size = 'medium',
      id,
      ...otherProps
    },
    ref,
  ) => {
    // Force en-US locale for consistent AM/PM formatting without periods
    const timeLocale = 'en-US'
    const [dropdownOpen, setDropdownOpen] = React.useState(false)

    // Internal state to handle controlled/uncontrolled component
    const [internalValue, setInternalValue] = React.useState<
      Temporal.PlainTime | undefined
    >(value || defaultValue)

    // Update internal state when props change
    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value)
      }
    }, [value])

    // Effective value (controlled via props or internal state)
    const effectiveValue = value !== undefined ? value : internalValue

    // Convert Temporal API objects to React Aria's expected format
    const timeValue = effectiveValue
      ? plainTimeToTimeValue(effectiveValue)
      : undefined
    const timeDefaultValue = defaultValue
      ? plainTimeToTimeValue(defaultValue)
      : undefined
    const timeMin = min ? plainTimeToTimeValue(min) : undefined
    const timeMax = max ? plainTimeToTimeValue(max) : undefined
    const timePlaceholder = placeholder
      ? plainTimeToTimeValue(placeholder)
      : undefined

    // Create state for time field
    const state = useTimeFieldState({
      value: timeValue,
      defaultValue: timeDefaultValue,
      onChange: (value) => {
        let newTime = value ? timeValueToPlainTime(value) : null
        // Clamp to min/max if defined
        if (newTime) {
          if (min && Temporal.PlainTime.compare(newTime, min) < 0) {
            newTime = min
          }
          if (max && Temporal.PlainTime.compare(newTime, max) > 0) {
            newTime = max
          }
        }
        // Update internal state if uncontrolled
        if (onChange === undefined) {
          setInternalValue(newTime || undefined)
        }
        // Call external onChange if provided
        else if (onChange) {
          onChange(newTime)
        }
      },
      locale: timeLocale,
      isDisabled: disabled,
      isReadOnly: readOnly,
      isRequired: required,
      hourCycle,
      granularity,
      minValue: timeMin,
      maxValue: timeMax,
      placeholderValue: timePlaceholder as TimeValue | undefined,
    })

    const innerRef = React.useRef<HTMLDivElement>(null)
    const mergedRef = useMergeRefs(ref, innerRef)

    const { fieldProps } = useTimeField(
      {
        ...otherProps,
        value: timeValue,
        defaultValue: timeDefaultValue,
        onChange: (value) => {
          if (onChange) {
            onChange(value ? timeValueToPlainTime(value) : null)
          }
        },
        granularity,
        minValue: timeMin,
        maxValue: timeMax,
        isDisabled: disabled,
        isReadOnly: readOnly,
        isRequired: required,
        hourCycle,
        id,
      },
      state,
      innerRef,
    )

    const dropdownTimeOptions = useMemo(
      () => getTimeOptions(hourCycle, granularity, min, max),
      [hourCycle, granularity, min, max],
    )

    const currentHour = effectiveValue?.hour ?? 0
    const currentMinute = effectiveValue?.minute ?? 0
    const isSelected = useCallback(
      (hour: number, minute: number): boolean => {
        if (granularity === 'hour') {
          return currentHour === hour
        } else {
          return currentHour === hour && currentMinute === minute
        }
      },
      [currentHour, currentMinute, granularity],
    )

    const handleTimeChange = (hour: number, minute: number) => {
      let newTime = new Temporal.PlainTime(hour, minute)
      // Clamp to min/max if defined
      if (min && Temporal.PlainTime.compare(newTime, min) < 0) {
        newTime = min
      }
      if (max && Temporal.PlainTime.compare(newTime, max) > 0) {
        newTime = max
      }
      if (onChange === undefined) {
        setInternalValue(newTime)
        state.setValue(plainTimeToTimeValue(newTime) as DateValue)
      } else if (onChange) {
        onChange(newTime)
      }
      setDropdownOpen(false)
    }

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.stopPropagation()
      }
      if (['Enter', ' '].includes(e.key)) {
        e.preventDefault()
        e.stopPropagation()
        if (!disabled && !readOnly && e.key === ' ') {
          setDropdownOpen((open) => !open)
        }
      }
    }

    const { itemRefs, scrollAreaRef } = useScrollToSelectedDropdownItem(
      dropdownOpen,
      dropdownTimeOptions,
      effectiveValue,
      granularity,
    )

    const handleInteractOutside = (e: HandleInteractOutsideEvent) => {
      const clickOutsideTrigger =
        e.type === 'dismissableLayer.pointerDownOutside' &&
        !isEventOverlappingRef(e, innerRef.current)
      const focusOutsideTrigger =
        e.type === 'dismissableLayer.focusOutside' &&
        !innerRef.current?.contains(e.target as Node)

      if (clickOutsideTrigger || focusOutsideTrigger) {
        setDropdownOpen(false)
      }
    }

    return (
      <DropdownMenu open={dropdownOpen} modal={false}>
        <DropdownMenu.Trigger
          asChild
          disabled={disabled || readOnly}
          onClick={() => {
            if (!disabled && !readOnly) {
              setDropdownOpen(true)
            }
          }}
        >
          <div
            {...fieldProps}
            ref={mergedRef}
            className={clsx(
              styles.timeField,
              styles[size],
              disabled && styles.disabled,
              className,
            )}
          >
            <div className={styles.timeSegmentsWrapper}>
              {state.segments.map((segment, i) => (
                <TimeSegment
                  key={i}
                  segment={segment}
                  state={state}
                  onKeyDown={onInputKeyDown}
                />
              ))}
            </div>
          </div>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          className={styles.dropdown}
          side="top"
          sideOffset={10}
          loop={true}
          onEscapeKeyDown={() => {
            setDropdownOpen(false)
          }}
          onInteractOutside={handleInteractOutside}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - onOpenAutoFocus exists on the underlying Radix primitive but is not exposed in the TypeScript types
          onOpenAutoFocus={(e: React.FocusEvent<HTMLDivElement>) => {
            // Prevent the dropdown from automatically focusing the first item
            // To keep the focus on the segment that triggered the dropdown
            e.preventDefault()
          }}
        >
          <ScrollArea className={styles.scrollArea} ref={scrollAreaRef}>
            {dropdownTimeOptions.map((opt, idx) => {
              const selected = isSelected(opt.hour, opt.minute)
              return (
                <DropdownMenu.Item
                  key={opt.label}
                  ref={(el) => (itemRefs.current[idx] = el)}
                  onSelect={() => handleTimeChange(opt.hour, opt.minute)}
                  disabled={disabled || readOnly}
                  className={clsx(styles.item, selected ? styles.selected : '')}
                  tabIndex={selected ? 0 : -1}
                >
                  {opt.label}
                </DropdownMenu.Item>
              )
            })}
          </ScrollArea>
        </DropdownMenu.Content>
      </DropdownMenu>
    )
  },
)

TimeInput.displayName = 'TimeInput'

/**
 * Generates time options for a 12-hour clock format
 *
 * @param granularity - The level of detail for time selection ('hour', 'minute', or 'second')
 * @returns An array of time options with hour, minute, label, and period (AM/PM)
 */
function getTimeOptions12Hour(
  granularity: 'hour' | 'minute' | 'second' = 'minute',
): { hour: number; minute: number; label: string; period?: DayPeriod }[] {
  const options: {
    hour: number
    minute: number
    label: string
    period?: DayPeriod
  }[] = []
  const step = granularity === 'hour' ? 60 : 15
  for (const period of ['AM', 'PM'] as DayPeriod[]) {
    for (let h = 1; h <= 12; h++) {
      for (let m = 0; m < 60; m += step) {
        if (granularity === 'hour' && m !== 0) continue
        const displayHour = h
        let hour = h % 12
        if (period === 'PM') hour += 12
        if (h === 12 && period === 'AM') hour = 0
        if (h === 12 && period === 'PM') hour = 12
        const label = `${displayHour.toString().padStart(2, '0')}:${m
          .toString()
          .padStart(2, '0')} ${period}`
        options.push({ hour, minute: m, label, period })
      }
    }
  }
  // Sort options by hour and minute to ensure chronological order
  options.sort((a, b) => {
    if (a.hour !== b.hour) return a.hour - b.hour
    return a.minute - b.minute
  })
  return options
}

/**
 * Generates time options for a 24-hour clock format
 *
 * @param granularity - The level of detail for time selection ('hour', 'minute', or 'second')
 * @returns An array of time options with hour, minute, and label
 */
function getTimeOptions24Hour(
  granularity: 'hour' | 'minute' | 'second' = 'minute',
): { hour: number; minute: number; label: string }[] {
  const options: { hour: number; minute: number; label: string }[] = []
  const step = granularity === 'hour' ? 60 : 15
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      if (granularity === 'hour' && m !== 0) continue
      const label = `${h.toString().padStart(2, '0')}:${m
        .toString()
        .padStart(2, '0')}`
      options.push({ hour: h, minute: m, label })
    }
  }
  return options
}

/**
 * Generates time options based on the specified hour cycle and granularity
 *
 * @param hourCycle - The hour cycle to use (12 or 24 hour format)
 * @param granularity - The level of detail for time selection ('hour', 'minute', or 'second')
 * @param min - The minimum allowed time
 * @param max - The maximum allowed time
 * @returns An array of time options with hour, minute, formatted label, and period (for 12-hour format)
 */
function getTimeOptions(
  hourCycle: 12 | 24,
  granularity: 'hour' | 'minute' | 'second' = 'minute',
  min?: Temporal.PlainTime,
  max?: Temporal.PlainTime,
): { hour: number; minute: number; label: string; period?: DayPeriod }[] {
  let options: {
    hour: number
    minute: number
    label: string
    period?: DayPeriod
  }[]
  if (hourCycle === 12) {
    options = getTimeOptions12Hour(granularity)
  } else {
    options = getTimeOptions24Hour(granularity)
  }
  if (min || max) {
    options = options.filter((opt) => {
      const t = new Temporal.PlainTime(opt.hour, opt.minute)
      if (min && Temporal.PlainTime.compare(t, min) < 0) return false
      if (max && Temporal.PlainTime.compare(t, max) > 0) return false
      return true
    })
  }
  return options
}

/**
 * Custom hook to scroll the selected or current time item to the center of the dropdown
 *
 * This hook manages scrolling behavior in the time dropdown to ensure the selected time
 * (or closest time if exact match not found) is visible and centered in the viewport.
 *
 * @param dropdownOpen - Boolean indicating if the dropdown is currently open
 * @param options - Array of time options with hour, minute, label, and optional period
 * @param currentValue - The current time value (if any)
 * @param granularity - The granularity of time selection (hour, minute, second)
 * @returns Object containing refs for the list items and scroll area
 */
function useScrollToSelectedDropdownItem(
  dropdownOpen: boolean,
  options: {
    hour: number
    minute: number
    second?: number
    label: string
    period?: DayPeriod
  }[],
  currentValue?: Temporal.PlainTime,
  granularity: 'hour' | 'minute' | 'second' = 'minute',
) {
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([])
  const scrollAreaRef = React.useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!dropdownOpen || options.length === 0) return

    let timeoutId: number | NodeJS.Timeout | null = null
    const rafId: number | null = null

    const scrollToItem = () => {
      let idx = -1

      if (currentValue) {
        // Try to find exact match for current value
        idx = options.findIndex((opt) => {
          if (granularity === 'hour') {
            return opt.hour === currentValue.hour
          } else {
            return (
              opt.hour === currentValue.hour &&
              opt.minute === currentValue.minute
            )
          }
        })

        if (idx === -1) {
          // If no exact match, find closest time
          idx = findClosestTimeIndex(
            options,
            currentValue.hour,
            currentValue.minute,
            currentValue.second,
            granularity,
          )
        }
      } else {
        // No current value, use current time
        const now = new Temporal.PlainTime()

        // Try to find exact match for current time
        idx = options.findIndex((opt) => {
          if (granularity === 'hour') {
            return opt.hour === now.hour
          } else {
            return opt.hour === now.hour && opt.minute === now.minute
          }
        })

        if (idx === -1) {
          // If current time not found, find closest time
          idx = findClosestTimeIndex(
            options,
            now.hour,
            now.minute,
            now.second,
            granularity,
          )
        }
      }

      // Ensure index is valid
      if (idx === -1 || idx >= options.length) {
        idx = 0 // Default to first option if no match found
      }

      const item = itemRefs.current[idx]
      const scrollArea = scrollAreaRef.current
      if (item && scrollArea) {
        const itemCenter = item.offsetTop + item.offsetHeight / 2
        const areaCenter = scrollArea.offsetHeight / 2
        scrollArea.scrollTop = itemCenter - areaCenter
      }
    }

    if (
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function'
    ) {
      window.requestAnimationFrame(scrollToItem)
    } else {
      timeoutId = setTimeout(scrollToItem, 0)
    }

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }, [dropdownOpen, options, currentValue, granularity])

  return { itemRefs, scrollAreaRef }
}

/**
 * Find the index of the closest time option to a given hour, minute and second
 *
 * @param options - Array of time options
 * @param hour - Target hour
 * @param minute - Target minute
 * @param second - Target second (optional)
 * @param granularity - The granularity of time selection
 * @returns Index of the closest time option
 */
function findClosestTimeIndex(
  options: { hour: number; minute: number; second?: number }[],
  hour: number,
  minute: number,
  second: number = 0,
  granularity: 'hour' | 'minute' | 'second' = 'minute',
): number {
  if (options.length === 0) return -1

  // Convert times to seconds for easier comparison with any granularity
  const targetSeconds =
    hour * 60 * 60 +
    (granularity !== 'hour' ? minute * 60 : 0) +
    (granularity === 'second' ? second : 0)

  let closestIndex = 0
  let smallestDiff = Infinity

  options.forEach((opt, index) => {
    const optSeconds =
      opt.hour * 60 * 60 +
      (granularity !== 'hour' ? opt.minute * 60 : 0) +
      (granularity === 'second' ? opt.second || 0 : 0)
    const diff = Math.abs(targetSeconds - optSeconds)

    if (diff < smallestDiff) {
      smallestDiff = diff
      closestIndex = index
    }
  })

  return closestIndex
}
